import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CellClickedEvent, ColDef, GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { setExpandedFolders, setIsReadOnly, setOriginalProduct } from 'store/productManagement/reducer';
import { changeProductStatus } from 'store/userOptions/reducer';
import { toggleDirectSalesProductStatus } from 'modules/api/directSalesProductTemplate';
import { cancelFunctions } from 'utils/cancelFunctions';
import Chip from 'lib/Chip';
import { Color, Size } from 'lib/Chip/Chip.types';
import { Store } from 'components/common/types/Store.types';
import { CodeNameModel } from 'components/common/types';
import {
  DirectSalesProductTemplateStatus,
  ProductIdentifier,
} from 'components/common/types/DirectSalesProductTemplate.types';
import useGetProductTemplates from 'components/pages/Planner/PlannerLineActions/DirectSalesPlannerLineActions/ProductTemplate/useGetProductTemplates';
import { capitalize } from 'lodash';
import Table from 'lib/Table/Table';
import { TableHeight, TableRowHeight } from 'lib/Table/Table.types';
import { getMarketId } from 'store/publisher/selectors';
import { useHasDealReadOnlyPermission } from 'customHooks/useHasDealReadOnlyPermission';
import { getFolderNames } from '../utils/getFolderNames';
import { searchProductByNameOrId } from '../utils/searchProductByNameOrId';
import { defaultAllFoldersProductOption, getProductsByFolderName } from '../utils/getProductsByFolderName';
import { getProductsToDisplayByStatus } from '../utils/getProductsToDisplayByStatus';
import ProductsTableFilters from './ProductsTableFilters';
import ProductOptionsMenu from '../ProductOptionsMenu';
import { sortProducts } from '../utils/sortProducts';

const ProductsTable: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const marketId = useSelector(getMarketId);
  const hasDealReadOnlyPermission = useHasDealReadOnlyPermission();
  const productList = useSelector((state: Store) => state.userOptions.productList);
  const expandedFolders = useSelector((state: Store) => state.productManagement.expandedFolders);
  const expandedKeysRef = useRef<Set<string>>(new Set());
  const [filteredProducts, setFilteredProducts] = useState<ProductIdentifier[]>(productList);
  const [isShowMoreMenuOpen, setIsShowMoreMenuOpen] = useState<string>('');
  const [selectedFolderGroup, setSelectedFolderGroup] = useState<string>(defaultAllFoldersProductOption.code);
  const [searchText, setSearchText] = useState<string>('');
  const [folders, setFolders] = useState<CodeNameModel[]>([]);

  useGetProductTemplates();

  useEffect(() => {
    const folderNames = getFolderNames(productList);

    setFolders(folderNames);
    setFilteredProducts(getProductsToDisplayByStatus({ isShowDisabled: false, products: productList }));
  }, [productList]);

  useEffect(() => {
    expandedKeysRef.current = new Set(expandedFolders);
  }, [expandedFolders]);

  const onChangeOfStatus = async (status: DirectSalesProductTemplateStatus, productId: string): Promise<void> => {
    try {
      const updatedProduct = await toggleDirectSalesProductStatus(
        { status: status === DirectSalesProductTemplateStatus.ACTIVE ? 'Disabled' : 'Active' },
        marketId,
        productId,
        cancelFunctions,
      );

      dispatch(changeProductStatus(updatedProduct));
      setIsShowMoreMenuOpen('');
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const onDuplicate = (
    originalProductId: string,
    intendedOriginalProductState: DirectSalesProductTemplateStatus,
  ): void => {
    dispatch(setOriginalProduct({ intendedOriginalProductState, originalProductId }));
    dispatch(setIsReadOnly(false));
    navigate('/product');
  };

  const onRowClick = (productId: string): void => {
    dispatch(setOriginalProduct({ originalProductId: productId }));
    dispatch(setIsReadOnly(true));
    navigate('/product');
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: 'Folder',
        rowGroup: true,
        field: 'folder',
        flex: 1,
        hide: true,
      },
      {
        headerName: '',
        field: 'id',
        enableRowGroup: true,
        cellRenderer: (params: ICellRendererParams) => {
          if (!params.node.group && !hasDealReadOnlyPermission) {
            return (
              <div className="w-full flex justify-center">
                <ProductOptionsMenu
                  isOpen={isShowMoreMenuOpen === params.value}
                  onToggle={() => setIsShowMoreMenuOpen(params.value)}
                  onKeepAndDuplicate={() => onDuplicate(params.value, DirectSalesProductTemplateStatus.ACTIVE)}
                  onDisableAndDuplicate={() => onDuplicate(params.value, DirectSalesProductTemplateStatus.DISABLED)}
                  onChangeOfStatus={() => onChangeOfStatus(params.data?.status, params.value)}
                  status={params.data?.status}
                />
              </div>
            );
          }
          return null;
        },
        resizable: false,
        maxWidth: 40,
      },
      {
        headerName: 'Folder / Status',
        field: 'status',
        enableRowGroup: true,
        cellRenderer: (params: ICellRendererParams) => (
          <div className="flex justify-center">
            <Chip
              label={capitalize(params.value)}
              color={params.value === DirectSalesProductTemplateStatus.ACTIVE ? Color.PRIMARY : Color.GREY}
              size={Size.MEDIUM}
            />
          </div>
        ),
        maxWidth: 120,
      },
      {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        resizable: false,
        cellRenderer: (params: ICellRendererParams) => (
          <span className="sub-header-base cursor-pointer hover:text-primary-700">{params.value}</span>
        ),
        onCellClicked: (params: CellClickedEvent) => {
          if (!params.node.group && params.data?.id) {
            onRowClick(params.data.id);
          }
        },
      },
    ],
    [isShowMoreMenuOpen, hasDealReadOnlyPermission],
  );

  const gridOptions: GridOptions<ProductIdentifier> = {
    autoGroupColumnDef: {
      cellRendererParams: {
        suppressCount: true,
      },
    },
    suppressAsyncEvents: true,
    defaultColDef: {
      resizable: true,
      suppressMenu: true,
    },
    suppressAggFuncInHeader: true,
    suppressMovableColumns: true,
    groupDisplayType: 'groupRows',
    getRowHeight: (params: ICellRendererParams) => (params.node.group ? TableRowHeight.SMALL : TableRowHeight.NORMAL),
    groupRowRendererParams: {
      innerRenderer: (params: ICellRendererParams) => <span className="body-base">{params.value}</span>,
    },
    onGridReady: (params: GridReadyEvent) => {
      params.api.sizeColumnsToFit();
    },
    isGroupOpenByDefault: (params) => {
      return expandedKeysRef.current.has(String(params.rowNode.key));
    },
    onRowGroupOpened: (event) => {
      const key = String(event.node.key);
      if (event.node.expanded) {
        expandedKeysRef.current.add(key);
      } else {
        expandedKeysRef.current.delete(key);
      }

      dispatch(setExpandedFolders(Array.from(expandedKeysRef.current)));

      setIsShowMoreMenuOpen('');
    },
    getRowId: (params) => params.data.id,
  };

  const onFilterTextChange = (searchTerm: string): void => {
    if (searchTerm.length === 0) {
      setFilteredProducts(productList);

      return;
    }

    if (searchTerm.length < 2) return;

    const foundProducts = searchProductByNameOrId({ products: productList, searchTerm });

    setFilteredProducts(foundProducts);
    setSearchText(searchTerm);
    setSelectedFolderGroup(defaultAllFoldersProductOption.code);
  };

  const onToggle = (isShowDisabled: boolean): void => {
    setFilteredProducts(getProductsToDisplayByStatus({ isShowDisabled, products: productList }));
  };

  const onChangeSelectedFolderGroup = (folderName: string): void => {
    setSelectedFolderGroup(folderName);
    setFilteredProducts(getProductsByFolderName({ folderName, products: productList }));
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b">
        <ProductsTableFilters
          folders={folders}
          selectedFolderGroup={selectedFolderGroup}
          onChangeSelectedFolderGroup={onChangeSelectedFolderGroup}
          onFilterTextChange={onFilterTextChange}
          searchText={searchText}
          onToggle={onToggle}
        />
      </div>
      <div className="p-3 flex-1">
        <Table
          columnDefs={columnDefs}
          rowData={sortProducts(filteredProducts)}
          extraGridOptions={gridOptions}
          tableHeight={TableHeight.FIT_SCREEN}
        />
      </div>
    </div>
  );
};

export default ProductsTable;
